import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { deferredUpdater, getBoardTitle, getId } from "../utils";
import * as jsonpatch from 'fast-json-patch';
import { Link } from 'react-router-dom';
import { Input } from './Input';
import { apiFetch } from '../api/api';
import { Loading } from './Loading';
import { WidgetDto } from './PageModels';

interface BoardDto {
    boardId: number;
    title: string;
    description: string;
    localization: string;
    boardVisualizationServices: {
        visualizationUnitId: string;
        title: string;
        status: string | null;
        isAssigned: boolean;
        boardId: number | null;
        isAssignable: boolean;
    }[];
    pages: {
        pageId: number;
        order: number;
        widgets: WidgetDto[];
    }[];
    accessRights: {
        userId: number;
        name: string;
        right: "Owner" | "Admin" | "View";
    }[];
}

interface State {
    board?: BoardDto;
    loading: boolean;
    addingBoardVisualizationService?: boolean;
    selectedVisualizationUnitId?: string;
    changingVisualizationUnitIds: string[];
}

export class Board extends React.Component<RouteComponentProps, State> {

    board?: BoardDto;
    serverBoard?: BoardDto;

    constructor(props: RouteComponentProps) {
        super(props);
        this.state = { loading: true, changingVisualizationUnitIds: [] };
    }

    componentDidMount() {
        this.init();
    }

    async init() {
        await deferredUpdater.updateNow();
        const id = getId(this.props);
        const response = id != null ?
            await apiFetch(`api/boards/${id}`) :
            await apiFetch(`api/boards`, { method: "POST" });
        if (!response.ok) {
            this.setState({ loading: false });
            return;
        }
        const board: BoardDto = await response.json();
        if (id === getId(this.props)) {
            this.setState({ board, loading: false });
            this.board = board;
            this.serverBoard = board;

            if (id == null) {
                this.props.history.replace(`/boards/${board.boardId}`);
            }
        }
    }

    componentDidUpdate(prevProps: RouteComponentProps, prevState: State) {
        const oldId = getId(prevProps);
        const newId = getId(this.props);
        if (oldId != null && newId != null && oldId !== newId) {
            this.init();
        }
    }

    render() {
        if (this.state.loading) {
            return <Loading />;
        }
        const board = this.state.board;
        if (!board) {
            return "Board failed to load.";
        }
        const dropdownServices = board.boardVisualizationServices.filter(p => !p.isAssigned)
            .concat(board.boardVisualizationServices.filter(p => p.isAssigned && p.boardId !== board.boardId));
        return <div>
            <nav>
                <ol className="breadcrumb">
                    <li className="breadcrumb-item"><Link to={`/`}>Boards</Link></li>
                    <li className="breadcrumb-item active"><Link to={`/boards/${board.boardId}`}>{getBoardTitle(board)}</Link></li>
                </ol>
            </nav>
            <h3>Parameters</h3>
            <div className="entity-parameters">
                <div className="form-group">
                    <span className="label">ID</span>
                    <span className="form-control">{board.boardId.toString()}</span>

                </div>
                <div className="form-group">
                    <span className="label">Title</span>
                    <Input className="form-control" value={board.title}
                        onBlur={title => { this.updateLocal({ title }); this.sendToServer() }} />
                </div>
                <div className="form-group"><span className="label">Description</span>
                    <input onChange={e => this.updateLocal({ description: e.target.value })}
                        onBlur={this.sendToServer} value={board.description || ""} className="form-control" />
                </div>
                <div className="form-group"><span className="label">Language</span>
                    <select value={board.localization || ""} className="form-control"
                        onChange={e => { this.updateLocal({ localization: e.target.value }); this.sendToServer() }}>
                        <option value="cs-CZ">Czech</option>
                        <option value="en-US">English</option>
                    </select>
                </div>
            </div>
            <h3>
                Pages
                <Link className="text-dark" to={`/boards/${board.boardId}/pages/new`}>
                    <i className="bi-plus-circle add-icon"></i>
                </Link>
            </h3>
            <table className="table">
                <thead>
                    <tr>
                        <th>Order</th>
                        <th>Content</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {board.pages.map((page, index) => <tr key={page.pageId}>
                        <td>{index + 1}</td>
                        <td>{page.widgets.map(widget => this.getWidgetDescription(widget)).join(", ")}</td>
                        <td>
                            <Link to={`/boards/${board.boardId}/pages/${page.pageId}`}>Edit</Link>
                            {" "}
                            <Link to="#" onClick={() => this.deletePage(page.pageId)}>Delete</Link>
                        </td>
                    </tr>)}
                </tbody>
            </table>
            <h3>
                Visualization endpoints
                <Link className="text-dark" to="#" onClick={() => this.setState({ addingBoardVisualizationService: true, selectedVisualizationUnitId: undefined })}>
                    <i className="bi-plus-circle add-icon"></i>
                </Link>
            </h3>
            <table className="table">
                <thead>
                    <tr>
                        <th>Title</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {board.boardVisualizationServices.filter(p => p.boardId).map(bvs => <tr key={bvs.visualizationUnitId}>
                        <td>{bvs.title}</td>
                        <td>{bvs.status}</td>
                        <td>
                            {!this.state.changingVisualizationUnitIds[bvs.visualizationUnitId] &&
                                <Link to="#" onClick={() => this.removeBoardVisualizationService(bvs.visualizationUnitId)}>Remove</Link>}
                        </td>
                    </tr>)}
                    {this.state.addingBoardVisualizationService && <tr>
                        <td>
                            <select className="form-control" value={this.state.selectedVisualizationUnitId || ""}
                                onChange={e => this.setState({ selectedVisualizationUnitId: e.target.value })}>
                                <option></option>
                                {board.boardVisualizationServices.filter(p => !p.isAssigned)
                                    .map(bvs => <option key={bvs.visualizationUnitId}
                                        value={bvs.visualizationUnitId}>{bvs.title + (bvs.isAssigned ? " (already assigned)" : "")}</option>)}
                            </select>
                        </td>
                        <td></td>
                        <td>
                            {this.state.selectedVisualizationUnitId &&
                                !this.state.changingVisualizationUnitIds[this.state.selectedVisualizationUnitId] &&
                                <Link to="#" onClick={() => this.addBoardVisualizationService()}>Add</Link>}
                            {" "}
                            {this.state.addingBoardVisualizationService &&
                                <Link to="#" onClick={() => this.setState({ addingBoardVisualizationService: false })}>Remove</Link>}
                        </td>
                    </tr>}
                </tbody>
            </table>
            <h3>
                Access rights
            </h3>
            <table className="table">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Rights</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {board.accessRights.map((user, index) => <tr key={user.userId}>
                        <td>{user.name}</td>
                        <td>{user.right}</td>
                        <td>
                            {user.right !== "Owner" && <Link to="#"
                                onClick={() => this.deleteAccessRight(user.userId)}>Delete</Link>}
                        </td>
                    </tr>)}
                </tbody>
            </table>
            <h3>Delete board</h3>
            <p>Deleted board cannot be restored.</p>
            <p><Link to={`#`} onClick={() => this.deleteBoard()}>Delete board</Link></p>
        </div>;
    }

    async removeBoardVisualizationService(visualizationUnitId: string) {
        let board = this.board!;
        board = {
            ...board,
            boardVisualizationServices: board.boardVisualizationServices
                .map(p => p.visualizationUnitId !== visualizationUnitId ? p :
                    ({ ...p, isAssigned: false, status: null, boardId: null }))
        };
        this.board = board;
        this.setState({
            board,
            changingVisualizationUnitIds: [...this.state.changingVisualizationUnitIds, visualizationUnitId],
        });

        await apiFetch(`/api/boards/${board.boardId}/VisualizationServices/${visualizationUnitId}`, {
            method: "DELETE",
        });

        board = this.board!;
        this.setState({
            changingVisualizationUnitIds: this.state.changingVisualizationUnitIds.filter(p => p !== visualizationUnitId),
        });
    }

    async addBoardVisualizationService() {
        const visualizationUnitId = this.state.selectedVisualizationUnitId!;
        let board = this.board!;
        board = {
            ...board,
            boardVisualizationServices: board.boardVisualizationServices
                .map(p => p.visualizationUnitId !== visualizationUnitId ? p :
                    ({ ...p, isAssigned: true, status: "Initializing", boardId: board.boardId })),
        };
        this.board = board;
        this.setState({
            board,
            addingBoardVisualizationService: false,
            selectedVisualizationUnitId: undefined,
            changingVisualizationUnitIds: [...this.state.changingVisualizationUnitIds, visualizationUnitId],
        });

        await apiFetch(`/api/boards/${board.boardId}/VisualizationServices`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ visualizationUnitId: visualizationUnitId }),
        });

        board = this.board!;
        board = {
            ...board,
            boardVisualizationServices: board.boardVisualizationServices.map(p =>
                !(p.visualizationUnitId === visualizationUnitId &&
                    p.isAssigned && p.status === "Initializing") ? p :
                    { ...p, status: "Pending" }),
        };
        this.board = board;
        this.setState({
            board: board,
            changingVisualizationUnitIds: this.state.changingVisualizationUnitIds.filter(p => p !== visualizationUnitId),
        });
    }

    async deleteBoard() {
        const board = this.board!;
        if (window.confirm(`Do you really want to delete the board "${getBoardTitle(board)}"?`)) {
            await apiFetch(`/api/boards/${this.state.board!.boardId}`, { method: "DELETE" });
            this.props.history.push("/");
        }
    }

    async deletePage(pageId: number) {
        if (!window.confirm(`Do you really want to delete the page?`)) {
            return;
        }
        const board = this.board!;
        this.board = {
            ...board,
            pages: board.pages.filter(p => p.pageId !== pageId),
        };
        this.setState({
            board: this.board,
        });
        await apiFetch(`/api/boards/${this.state.board!.boardId}/pages/${pageId}`, { method: "DELETE" });
    }

    async deleteAccessRight(userId: number) {
        if (!window.confirm(`Do you really want to delete the access right?`)) {
            return;
        }
        const board = this.board!;
        this.board = {
            ...board,
            accessRights: board.accessRights.filter(p => p.userId !== userId),
        };
        this.setState({
            board: this.board,
        });
        await apiFetch(`/api/boards/${this.state.board!.boardId}/accessRights/${userId}`, { method: "DELETE" });
    }

    getWidgetDescription(widget: WidgetDto) {
        return widget.type + (widget.type === "online" ? ": " + (widget.onlineRows?.map(p => p.measurementId).join(", ") || "-") : "");
    }

    updateLocal = async (value: Partial<BoardDto>) => {
        const board: BoardDto = { ...this.board!, ...value };
        this.setState({ board });
        this.board = board;
    }

    sendToServer = async () => {
        deferredUpdater.scheduleUpdate(async () => {
            const board = this.board!;
            const patch = jsonpatch.compare(this.serverBoard!, board);
            if (patch.length > 0) {
                await apiFetch(`api/boards/${this.board!.boardId}`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/jsonpatch+json" },
                    body: JSON.stringify(patch),
                });
                this.serverBoard = board;
            }
        });
    }
}
